/**
 * Normalized GTM telemetry event model.
 *
 * Every interaction the framework captures is pushed to the dataLayer as a single
 * normalized shape so GTM/GA4 can run on one Custom Event trigger + one GA4 tag with
 * reusable Data Layer Variables (no per-page lookup tables).
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/events
 */

/** Normalized custom event name for attributed click interactions. */
export const TELEMETRY_CLICK = 'telemetry_click' as const;

/**
 * The normalized telemetry event shape pushed to the dataLayer.
 *
 * `event` is the dataLayer key GTM's Custom Event trigger listens on. The remaining
 * fields map to reusable Data Layer Variables consumed by the single GA4 tag.
 */
export interface TelemetryEvent {
  /** dataLayer event name (e.g. `telemetry_click`). */
  event: string;
  /**
   * Stable, unique key for the interaction, sourced from `data-telemetry-name`.
   * This is the primary GA4 dimension used to distinguish one tracked element from
   * another, independent of visible label or dynamic id.
   */
  name?: string;
  /** Logical grouping the element belongs to (e.g. `orders_view`). */
  section?: string;
  /** Interaction verb/type (e.g. `click`). */
  action?: string;
  /** Human-readable label for the interaction. */
  label?: string;
  /** Dynamic identifier (order id, product id) when applicable. */
  id?: string;
  /** Additional non-PII context. */
  metadata?: Record<string, unknown>;
}
